import { TaskType, VoiceInfoType } from '@/pages/api/cld';
import { ModelTypeEnum } from '@/utils/const';
import { t } from '@/utils/helper/intl';
import request from '@/utils/request';

export interface ApiResponse<T> {
  code: number;
  hasMore: boolean;
  items?: T[];
  data?: T | T[];
  page: number;
  pageSize: number;
  totalCount: number;
  success: boolean;
  message?: string;
  [key: string]: any;
}

export interface ApiResult<T> {
  code: number;
  data?: T;
  items?: T;
  message?: string;
  count?: number;
  success?: boolean;
  isError?: boolean;
}
/**
 * 获取上传token
 * @param fileName 文件名
 * @param dirSceneType 10-用户头像 20-资产 30-prj 40-语音接管 50-临时资产
 * @returns
 */
export const fetchToken = async (fileName: string, dirSceneType: number) => {
  const res = await request('/api/rest/dlt/oss/upload/token', {
    method: 'POST',
    data: {
      fileName,
      dirSceneType,
    },
  });
  return res;
};

export interface IAssetProps {
  goodsId?: number;
  type: number; //type 1-文件夹，2-图片，3-视频，4-字体,5-音频,6- 音色音频 ，7- zip 文件,8-文本
  title?: string;
  desc?: string;
  fileUrl?: string; // 文件地址或者文本内容
  duration?: number; // 音频时长
  cover?: string;
  customType?: string;
  id?: number;
  text?: string;
  [prop: string]: any;
}

// 查询资产
export const getAssetList = async (data: any): Promise<any> => {
  const res = await request('/api/rest/dlt/asset/query', {
    method: 'POST',
    data,
  });
  return res;
};

// 更新资产
export const updateAsset = async (data: IAssetProps): Promise<ApiResult<string>> => {
  const res = await request('/api/rest/dlt/asset/update', {
    method: 'POST',
    data,
  });
  return res;
};

export const saveAsset = async (data: IAssetProps): Promise<ApiResult<any>> => {
  const res = await request('/api/rest/dlt/asset/add', {
    method: 'POST',
    data,
  });
  return res;
};
export const saveBatchAsset = async <T>(data: IAssetProps[]): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/asset/batch/add', {
    method: 'POST',
    data: {
      assetList: data,
    },
  });
  return res;
};

// 允许的语言类型
const allowedLanguagesSet = new Set([
  'en',
  'ja',
  'ko',
  'id',
  'th',
  'tl',
  'es',
  'ru',
  'de',
  'ar',
  'ms',
  'ta',
  'vi',
]);

type AllowedLanguages = typeof allowedLanguagesSet extends Set<infer T> ? T : never;

type LangMap = {
  [TaskType.lang]: AllowedLanguages[];
  [TaskType.text]: string[];
};
// 接口请求类型
export interface ILanguage<T extends keyof LangMap> {
  // 任务类型: 1|2, 分别代表直接传递语言类型检测（如视频翻译）和检测文本（唇音驱动、脚本、视频翻译二次编辑）
  type: T;
  // 需要校验语言的文本，如tts文本
  input: LangMap[T];
  // 当前选择的音色信息，要校验的话必须包含programType或者immutableExtend的其中一个字段
  // 没有就直接返回成功，相当于没校验
  voiceInfo: VoiceInfoType[];
}

//接口返回格式
export interface IValidResult {
  // 接口是否成功
  code: number;
  // 失败的话-失败信息
  message: string;
  // 接口成功的话有data字段，失败为null
  data: {
    // 校验结果
    success: boolean;
    // 失败时，不符合的语言
    // type为1时，不符合的语言是你传入的input数据中的一种或多种
    // type为2时，不符合的语言信息是franc校验返回的
    // franc的具体的语言枚举看这里：https://github.com/wooorm/franc/blob/main/packages/franc/data.js
    failLangs: string[];
    // 返回具体出错的音色信息数组对应的索引，如传入的第三个音色校验的某个语言不通过，则格式为: errorInfo: {2: ['ru']}
    errorInfo: {
      [index: number | string]: string[];
    };
    message?: string;
  } | null;
}

// 校验当前文本语言音色是否符合
export const validateLanguage = async (
  data: ILanguage<TaskType>,
): Promise<IValidResult> => {
  const res = await request(
    '/api/cld',
    {
      method: 'POST',
      data,
    },
    true,
    true,
  );
  if (res && res.data) {
    try {
      const { success = true, failLangs = [] } = res.data || {};
      if (!success) {
        const errorStr = getErrLang(failLangs);
        res.data.message = t('merror.language.failTip', {
          language: errorStr,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  return res;
};

// 处理下获取到的错误语言信息
export const getErrLang = (failLangs: string[]) => {
  const failLang = failLangs.map((lang: string) => {
    return t(lang) || t('und');
  });
  const error = [...new Set(failLang)];
  const errorStr = error.join(',');
  return errorStr;
};

// 获取模型计费信息
export const fetchModelCredit = async (data: {
  modelID: number;
  params: string;
}): Promise<ApiResult<{ basePrice: number; amount: number; discount: number }>> => {
  const res = await request('/api/rest/dlt/f/cloud/modelFee', {
    method: 'POST',
    data,
  });
  return res;
};

// 提交模型接口
export const submitModelTask = async (data: {
  modelID: number;
  modelType: ModelTypeEnum;
  params: string;
  ecoParams?: Record<string, any>;
}): Promise<ApiResult<{ taskId: string }>> => {
  const res = await request('/api/rest/dlt/f/cloud/model/submit', {
    method: 'POST',
    data,
  });
  return res;
};

export const downloadTaskFile = async (assetIds: number[]) => {
  const res = await request('/api/rest/dlt/f/cloud/down', {
    method: 'POST',
    data: {
      assetIds,
    },
  });
  return res;
};

/**
 * 重命名资产
 */
export const renameAsset = async <T>(params: {
  id: number;
  title: string;
}): Promise<ApiResponse<T>> => {
  const res = await request('/api/rest/dlt/asset/update', {
    method: 'POST',
    data: params,
  });
  return res;
};
